<template>
  <el-row>
    <el-row
      :gutter="8"
      class="pb-2"
    >
      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ estimatedSpendCurrentFormatted }}
            <small
              v-if="isFinite(estimatedSpendDifference) && estimatedSpendDifference !== 0"
              class="text-info"
            >
              <FontAwesomeIcon
                :icon="['fas', (estimatedSpendDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ estimatedSpendPreviousFormatted }}
                </div>
                <small>{{ Math.round(Math.abs(estimatedSpendDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Estimated Spend</small>
          </h1>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ averageConsignmentCostCurrentFormatted }}
            <small
              v-if="isFinite(averageConsignmentCostDifference) && averageConsignmentCostDifference !== 0"
              class="text-info"
            >
              <FontAwesomeIcon
                :icon="['fas', (averageConsignmentCostDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ averageConsignmentCostPreviousFormatted }}
                </div>
                <small>{{ Math.round(Math.abs(averageConsignmentCostDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Avg. Consignment Cost</small>
          </h1>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ totalConsignmentsCurrentFormatted }}
            <small
              v-if="isFinite(totalConsignmentsDifference) && totalConsignmentsDifference !== 0"
              class="text-info"
            >
              <FontAwesomeIcon
                :icon="['fas', (totalConsignmentsDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ totalConsignmentsPreviousFormatted }}
                </div>
                <small>{{ Math.round(Math.abs(totalConsignmentsDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Total Consignments</small>
          </h1>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="8">
      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ totalItemsCurrentFormatted }}
            <small
              v-if="isFinite(totalItemsDifference) && totalItemsDifference !== 0"
              class="text-info"
            >
              <FontAwesomeIcon
                :icon="['fas', (totalItemsDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ totalItemsPreviousFormatted }}
                </div>
                <small>{{ Math.round(Math.abs(totalItemsDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Total Items</small>
          </h1>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ totalVolumeCurrentFormatted }}<small>m<sup>3</sup></small>
            <small
              v-if="isFinite(totalVolumeDifference) && totalVolumeDifference !== 0"
              class="text-info ml-1"
            >
              <FontAwesomeIcon
                :icon="['fas', (totalVolumeDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ totalVolumePreviousFormatted }}<small>m<sup>3</sup></small>
                </div>
                <small>{{ Math.round(Math.abs(totalVolumeDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Total Volume</small>
          </h1>
        </el-card>
      </el-col>

      <el-col :span="8">
        <el-card shadow="never">
          <h1 class="font-weight-bold">
            {{ totalWeightCurrentFormatted }}<small>kg</small>
            <small
              v-if="isFinite(totalWeightDifference) && totalWeightDifference !== 0"
              class="text-info ml-1"
            >
              <FontAwesomeIcon
                :icon="['fas', (totalWeightDifference > 0 ? 'chevron-up' : 'chevron-down')]"
                size="md"
                fixed-width
              />
              <el-tooltip
                effect="dark"
                placement="right"
              >
                <div slot="content">
                  Previous period: {{ totalWeightPreviousFormatted }}<small>kg</small>
                </div>
                <small>{{ Math.round(Math.abs(totalWeightDifference)) }}%</small>
              </el-tooltip>
            </small>
            <br>
            <small class="font-weight-normal">Total Weight</small>
          </h1>
        </el-card>
      </el-col>
    </el-row>
  </el-row>
</template>

<script>
export default {
  props: {
    statistics: {
      type: Object,
      required: true
    }
  },
  computed: {
    estimatedSpendCurrent () {
      let current = 0

      if ('estimatedSpend' in this.statistics) {
        current = this.statistics.estimatedSpend.current
      }

      return current
    },
    estimatedSpendCurrentFormatted () {
      return this.formatCurrency(this.estimatedSpendCurrent).slice(0, -3)
    },
    estimatedSpendPrevious () {
      let previous = 0

      if ('estimatedSpend' in this.statistics) {
        previous = this.statistics.estimatedSpend.previous
      }

      return previous
    },
    estimatedSpendPreviousFormatted () {
      return this.formatCurrency(this.estimatedSpendPrevious).slice(0, -3)
    },
    estimatedSpendDifference () {
      return this.calculateDifference(this.estimatedSpendCurrent, this.estimatedSpendPrevious)
    },
    averageConsignmentCostCurrent () {
      let current = 0

      if ('averageConsignmentCost' in this.statistics) {
        current = this.statistics.averageConsignmentCost.current
      }

      return current
    },
    averageConsignmentCostCurrentFormatted () {
      return this.formatCurrency(this.averageConsignmentCostCurrent)
    },
    averageConsignmentCostPrevious () {
      let previous = 0

      if ('averageConsignmentCost' in this.statistics) {
        previous = this.statistics.averageConsignmentCost.previous
      }

      return previous
    },
    averageConsignmentCostPreviousFormatted () {
      return this.formatCurrency(this.averageConsignmentCostPrevious)
    },
    averageConsignmentCostDifference () {
      return this.calculateDifference(this.averageConsignmentCostCurrent, this.averageConsignmentCostPrevious)
    },
    totalConsignmentsCurrent () {
      let current = 0

      if ('totalConsignments' in this.statistics) {
        current = this.statistics.totalConsignments.current
      }

      return current
    },
    totalConsignmentsCurrentFormatted () {
      return this.formatNumber(this.totalConsignmentsCurrent)
    },
    totalConsignmentsPrevious () {
      let previous = 0

      if ('totalConsignments' in this.statistics) {
        previous = this.statistics.totalConsignments.previous
      }

      return previous
    },
    totalConsignmentsPreviousFormatted () {
      return this.formatNumber(this.totalConsignmentsPrevious)
    },
    totalConsignmentsDifference () {
      return this.calculateDifference(this.totalConsignmentsCurrent, this.totalConsignmentsPrevious)
    },
    totalItemsCurrent () {
      let current = 0

      if ('totalItems' in this.statistics) {
        current = this.statistics.totalItems.current
      }

      return current
    },
    totalItemsCurrentFormatted () {
      return this.formatNumber(this.totalItemsCurrent)
    },
    totalItemsPrevious () {
      let previous = 0

      if ('totalItems' in this.statistics) {
        previous = this.statistics.totalItems.previous
      }

      return previous
    },
    totalItemsPreviousFormatted () {
      return this.formatNumber(this.totalItemsPrevious)
    },
    totalItemsDifference () {
      return this.calculateDifference(this.totalItemsCurrent, this.totalItemsPrevious)
    },
    totalVolumeCurrent () {
      let current = 0

      if ('totalVolume' in this.statistics) {
        current = this.statistics.totalVolume.current
      }

      return current
    },
    totalVolumeCurrentFormatted () {
      return this.formatNumber(this.totalVolumeCurrent)
    },
    totalVolumePrevious () {
      let previous = 0

      if ('totalVolume' in this.statistics) {
        previous = this.statistics.totalVolume.previous
      }

      return previous
    },
    totalVolumePreviousFormatted () {
      return this.formatNumber(this.totalVolumePrevious)
    },
    totalVolumeDifference () {
      return this.calculateDifference(this.totalVolumeCurrent, this.totalVolumePrevious)
    },
    totalWeightCurrent () {
      let current = 0

      if ('totalWeight' in this.statistics) {
        current = this.statistics.totalWeight.current
      }

      return current
    },
    totalWeightCurrentFormatted () {
      return this.formatNumber(this.totalWeightCurrent)
    },
    totalWeightPrevious () {
      let previous = 0

      if ('totalWeight' in this.statistics) {
        previous = this.statistics.totalWeight.previous
      }

      return previous
    },
    totalWeightPreviousFormatted () {
      return this.formatNumber(this.totalWeightPrevious)
    },
    totalWeightDifference () {
      return this.calculateDifference(this.totalWeightCurrent, this.totalWeightPrevious)
    }
  },
  methods: {
    calculateDifference (current, previous) {
      const difference = ((current - previous) / previous) * 100

      return (Number.isNaN(difference) ? 0 : difference)
    },
    formatCurrency (value) {
      return (new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      })).format(value)
    },
    formatNumber (value) {
      return (new Intl.NumberFormat('en-US')).format(value)
    }
  }
}
</script>
